// MSB 아바타 프레젠터 카탈로그 — 이미 만들어둔 회사 아바타 클립(생성·비용 0) 정본.
//
// 데이터 모델은 crabs 프로덕션 info-video `InfoVideoCharacterSpec`(imageUrl·chromaKey·motions)의 축약형 —
// 추후 serjau 테이블/avatar-performer 연동으로 승격 시 이 shape 를 행으로 옮기면 된다(UI 무변경).
// ★클립 특성(2026-07-20 픽셀 실측 정정 — 클립별 상이): wkwk-hyundai 4클립=마젠타 크로마 구움
//   (배경 [254,37,228] 불투명 — WebGL 키잉으로 컷아웃), wkwk-alpha-a=★실제 알파 WebM(배경 [0,0,0,0]).
//   ChromaVideo 셰이더가 최종 알파에 원본 알파를 곱해 양쪽을 단일 파이프라인으로 처리한다
//   (원본 알파 무시 시 알파 소스의 투명 배경이 검정 박스로 되살아나는 실사고). keyColor 미지정=마젠타.

export interface MsbAvatarPreset {
  id: string;
  label: string;
  description: string;
  kind: "chroma";
  /** 크로마 키 컬러(0~1 RGB) — 미지정=마젠타. */
  keyColor?: [number, number, number];
  /** M 씬에 순서대로 적용(부족하면 순환). */
  urls: string[];
  /** 🎬 Sync.so 영상 립싱크 가능(3.5c) — 크로마 배경 구움 클립만 true. 알파 WebM(아바타A)은
   *  Sync.so 가 알파를 솔리드 배경으로 평탄화(공식 문서)해 키잉 색이 불명이라 제외. */
  lipsyncCapable?: boolean;
  /** 🏢 회사 전용 인물 — 이 업종(`style-categories` INDUSTRY_OPTIONS value) 회사의 픽커에만 선다(#6344).
   *  미지정 = 범용(전 회사). ★회사 이름이 아니라 업종으로 가른다★ — 같은 업종 고객사가 늘어도 규칙 한 줄이다.
   *  렌더(`getMsbAvatarPreset`)는 이 칸을 보지 않는다 — 이미 고른 작업물은 업종과 무관하게 그대로 나온다. */
  industries?: readonly string[];
  /** 🎙 이 인물과 짝인 목소리(`voice-catalog` id) — 이 인물을 켠 작업물의 목소리 창이 ★아직 고른 목소리가 없을 때★
   *  이 목소리로 열린다(회장님 10-09 «소장 목소리는 현대건설 아바타와 짝»). 유저가 고른 목소리는 덮지 않는다. */
  voiceId?: string;
  /** 🧍 상반신 «진행자» 구도 — 원본이 허리 위까지 찍힌 인물. 오른쪽 넓은 칸에 화면 높이를 채워 크게 세우고
   *  장표·자막을 왼쪽으로 민다(`PRESENTER_HOST` · [건설] 1편 «사람이 구석에 작게» 10-10). 미지정 = 종전 27% 칸. */
  framing?: "bust";
  /** 🧍 상반신 구도 칸 폭(화면 %) — 원본 비율이 다르면 칸을 맞춘다(세로 1276×1620 = 42 · 정사각 손짓 여백 클립 = 56). 미지정 = 42. */
  framingWidthPct?: number;
  /** 🧍 상반신 칸 확대 배율(아래 기준 · 넘친 위는 화면 밖) — 머리 위 여백이 큰 원본의 얼굴을 올린다. 미지정 = 1. */
  framingScale?: number;
  /** 🧍 상반신 칸 오른쪽 걸침(화면 %) — 확대로 몸이 왼쪽 카드에 닿지 않게 오른쪽으로 민다. 미지정 = -1. */
  framingRightPct?: number;
  /** 🖐 클립 역할(urls 와 같은 순서) — «대기»·«손짓». 있으면 아침 조회 장표는 역할로 고른다(`presetClipUrlForScene`). 없으면 종전 순번 순환. */
  clipRoles?: Array<"idle" | "point">;
  /**
   * 🎬 장면마다 그 대사로 따로 굽기(회장님 «장면별 굽기 ① 가» 10-11 · `presenter-scene-bake`) — 있으면 립싱크 생성이
   *   장면별로 씨댄스를 구운 뒤 1배속 립싱크한다. 굽기 실패·15초 초과 장면은 이 프리셋 `urls`(clipRoles)로 대신한다.
   *   ★첫 그림은 재고 클립을 구운 그 정사각 원본 그대로★ — 같은 구도라 작업물 구도 값(framing*)이 한 벌이다.
   *   16:9·4:3 으로 둘레를 넓힌 그림은 씨댄스가 «실사 인물»로 거부했다(10-11 dev 3/3 · 정사각은 통과 3/3).
   */
  sceneBake?: { firstFrameUrl: string };
}

/** 🖐 아침 조회 — 소장이 손바닥으로 왼쪽 카드를 가리키는 장면(기본 수칙·날씨·특히 주의). 표지·사고·구호·두 가지는 대기.
 *  ✋ «두 가지»(morningActions)는 손짓 없음(회장님 10-11 «손이 잘리는데») — 그 장면은 진행자를 장표 뒤에 그려서(뻗은 손이 둘째 카드 글자를
 *  가리지 않게) 뻗은 손이 카드에 가려 «잘린 손»으로 보였다. */
const MORNING_POINT_TYPES: ReadonlySet<string> = new Set(["morningWeather", "morningFocus"]);
const MORNING_TYPES: ReadonlySet<string> = new Set(["morningCover", "morningChecks", "morningWeather", "morningFocus", "morningCase", "morningActions"]);

/** 아침 조회 장표의 진행자 역할(손짓·대기) — 아침 조회 장표가 아니면 null. 재고 클립 고르기·장면별 굽기 지시가 같은 함수를 읽는다. */
export function morningSceneRole(slide?: { type?: string; content?: Record<string, unknown> } | null): "idle" | "point" | null {
  const type = slide?.type ?? "";
  if (!MORNING_TYPES.has(type)) return null;
  return MORNING_POINT_TYPES.has(type) || (type === "morningChecks" && slide?.content?.mode !== "chant") ? "point" : "idle";
}

/** 장표 씬 순번 ordinal 의 프리셋 클립 — 미리보기(Overlay169)와 립싱크 원본(generateBlueprintPresenterClips)이 ★같은 함수★로 고른다.
 *  아침 조회 장표 + 역할 표시가 있는 프리셋만 역할로 고르고, 그 역할 클립이 없으면 대기 클립(손짓 클립을 뺀 프리셋 = 늘 대기).
 *  그 밖은 종전 그대로(순번 순환 · 다른 작업물 바이트 동일). */
export function presetClipUrlForScene(
  preset: Pick<MsbAvatarPreset, "urls" | "clipRoles">,
  ordinal: number,
  slide?: { type?: string; content?: Record<string, unknown> } | null,
): string | undefined {
  const roles = preset.clipRoles;
  const role = morningSceneRole(slide);
  if (roles && roles.length === preset.urls.length && role) {
    const idx = roles.indexOf(role) >= 0 ? roles.indexOf(role) : roles.indexOf("idle");
    if (idx >= 0) return preset.urls[idx];
  }
  return preset.urls[((ordinal % preset.urls.length) + preset.urls.length) % preset.urls.length];
}

export const MSB_AVATAR_PRESETS: MsbAvatarPreset[] = [
  {
    id: "wkwk-alpha-a",
    label: "아바타 A",
    description: "범용 프레젠터 · 데모 발화",
    kind: "chroma",
    urls: ["https://dev-resource2.wkwk.ai/vibemotion/avatar-lipsync/71d9a7ecbc8487fc2363081f.webm"],
  },
  {
    id: "wkwk-hyundai",
    label: "현대건설 안전 아바타",
    description: "안전모·조끼 착용 프레젠터 · 씬별 4클립 순환",
    kind: "chroma",
    lipsyncCapable: true, // 마젠타 크로마 구움 — Sync.so remap 배경 보존 실측(E1/E2)
    industries: ["construction"], // 현대건설 브랜드 인물 — 건설 업종 회사에만(#6344 · 삼성 등 타 업종 픽커 노출 결함)
    // 소장 목소리 = 구글 «Sadachbia»(남성·낮은 · 10-12 새벽 회장님 «음성은 3번 구글 예비(Sadachbia)가 제일 좋네. 저걸로 해라»).
    //   아침 조회 1편 전체 대본 비교(맥공유 산업안전/소장아침조회_지침/소장목소리_후보_10-11/전체대본_60초) — 쉼이 많고 느슨한 결이
    //   회장님이 고른 맛이다 → 속도·쉼을 손대지 않는다(기본값).
    //   그전: «빈»(얼굴보다 젊음) → «영길»(«너무 할아버지 톤·부자연») → «현빈»(10-11 밤 선택 · 전체 대본 듣고 바꿈).
    //   건설 용어 TTS 발음 규칙(10-09·10-11)은 목소리와 무관하게 전역으로 걸린다.
    voiceId: "gemini:Sadachbia",
    urls: [
      "https://dev-resource2.wkwk.ai/vibemotion/avatar-lipsync/ddf58e47f5d45276d7972ac0.webm",
      "https://dev-resource2.wkwk.ai/vibemotion/avatar-lipsync/29c7f152b4daea556ce81011.webm",
      "https://dev-resource2.wkwk.ai/vibemotion/avatar-lipsync/77fc4d52307b48e287b74f11.webm",
      "https://dev-resource2.wkwk.ai/vibemotion/avatar-lipsync/fbf931000ac63da14cdeced6.webm",
    ],
  },
  {
    // 🏗🌅 현장소장(스튜디오 · 10-10 회장님 «응 교체해») — v3 «아침 안전 조회» 소장(AI 인물 · 글자·로고 없음)을 분홍 배경으로 옮겨
    //   씨댄스 2.0 으로 구운 정사각 클립 2개: [0] 대기(손 모음) · [1] 오른손 펴서 왼쪽 가리킴. 왼쪽 25% 가 손짓 여백이라 칸을 넓힌다(56%).
    //   검사(10-10): 1초 간격 가짜 글자 0 · 키잉 뒤 보라 0 · 손 프레임 안(최소 x 58/960) · 배경 표준편차 ≤ 2.2 · v3 c2 와 같은 얼굴.
    id: "wkwk-sojang-studio",
    label: "현장소장(스튜디오)",
    description: "아침 조회 소장 · 글자·로고 없음",
    kind: "chroma",
    lipsyncCapable: true,
    industries: ["construction"],
    voiceId: "gemini:Sadachbia", // Sadachbia — 현대건설 안전 아바타와 같은 소장 목소리(10-12 회장님)
    framing: "bust", // 🧍 상반신 구도 표시는 이 한 줄 — 인물을 바꾸면 이 줄만 옮긴다(본부 10-10)
    framingWidthPct: 56,
    // 🧍 3단계 v2(10-11 · 이치죠 §7 «소장이 작고 아래로»): 정사각 원본은 머리 위 여백이 커 얼굴이 v3 보다 ≈190px 아래 · 몸 폭 13% 좁았다 → 1.18배 · 오른쪽 6% 걸침.
    framingScale: 1.18,
    framingRightPct: -6,
    // ★우리 R2(crabs-videos · presenter-presets/)★ — VP9 webm(현대건설 클립과 같은 형식) · 씨댄스 결과(sedeo_cgt-…mp4)에서 옮긴 사본.
    //   관제 승격 10-11(HTTP 200 · video/webm · CORS * · sha256 맥공유 원본 일치) — 크로마 키어가 픽셀을 cross-origin 으로 읽으므로 CORS * 필수.
    //   🔁 20초 왕복판(10초 원본 앞→뒤 · 3단계 10-11): 립싱크 벤더는 장면 음성이 원본보다 길면 클립을 늘려(remap) 꼬리에서 입이 닫힌다(1편 원인) —
    //   아침 조회 장면 음성 최장 ≈11초라 20초 원본이면 늘릴 일이 없다. 10초판도 R2 에 남아 있다(옛 작업물 립싱크 원본).
    //   🗣 «말하는» 원본(3단계 v2 · 회장님 «추천대로 1번 가» 10-11): 입 다문 원본(loop20-idle/point · «Lips closed, not talking»)은 립싱크가 입을 거의 안 벌렸다 →
    //   말하며 입이 움직이는 원본으로 바꾸니 벌어짐(입 안 어두운 면적 2배 · 4초 장면 실측). 손짓판은 말하면서 왼쪽 카드 쪽으로 팔을 뻗는다. 옛 loop20 은 R2 에 그대로.
    //   ✋ 손짓판(loop20-talkpoint-ffgfq)은 뺐다(회장님 10-11 «손이 잘리는데») — 손끝이 원본 왼쪽 끝에 4초 닿아(96프레임) 화면에선 손목 없는
    //   손이 떴다. 손짓은 장면별 굽기(그 장면 대사로 · 가장자리 검사 통과분만)가 맡고, 재고·대체는 대기판 하나다. 손짓판 파일은 R2 에 남아 있다.
    urls: [
      "https://pub-6e875c782c654b92bb480138c507492a.r2.dev/presenter-presets/sojang-studio/loop20-talkidle-t45st.webm",
    ],
    clipRoles: ["idle"],
    // 🎬 장면별 굽기(10-11) — 첫 그림 = 위 재고 클립을 구운 정사각 원본(분홍 · 소장 가운데 오른쪽). 대체 = 위 urls.
    sceneBake: {
      firstFrameUrl: "https://pub-6e875c782c654b92bb480138c507492a.r2.dev/presenter-presets/sojang-studio/scene-bake-first-1x1.png",
    },
  },
];

export const getMsbAvatarPreset = (id: string | null | undefined): MsbAvatarPreset | undefined =>
  MSB_AVATAR_PRESETS.find((p) => p.id === id);

/** 🎙 작업물의 아바타 상태(`StoryTemplate.presenter` 모양)에서 짝 목소리 id — 프리셋을 켰고 그 인물에 짝이 있을 때만. */
export function presenterPairedVoiceId(presenter: unknown): string | null {
  const id = (presenter as { presetId?: unknown } | null | undefined)?.presetId;
  return typeof id === "string" ? getMsbAvatarPreset(id)?.voiceId ?? null : null;
}

/** 🧍 상반신 구도일 때의 장표·자막 배치(렌더 `Overlay169Template` · 정밀 편집 `precision-source-slides` 가 같이 읽는다) —
 *  장표는 왼쪽 60% 안(오른쪽 40% 비움), 자막은 하단 왼쪽(왼쪽 5%에서 시작 · 글 폭 55% · 최대 2줄).
 *  ★글 폭 55%★(10-10 v3 나란히 비교) — v3 자막 한 줄(«10월 10일 토요일, 아침 안전 조회 시작하겠습니다.» · 48px)이 50% 로는 두 줄로
 *  접혀 띠가 높아지고 장표가 밀렸다. 띠 끝이 소장 팔 앞(≈ 63%)까지 오는 것은 v3 도 같다. */
// 💬 captionWidthRatio 0.6(10-11 3단계 v2) — v3 실측 자막 띠 977px 에 «10월 10일 토요일, 아침 안전 조회 시작하겠습니다.»(접기 어림 `glyphEm` 23.24em)가 한 줄. 0.55(22em)면 그 문장이 두 줄로 접혔다.
//   접기 한도일 뿐 띠는 글에 맞춰 줄어든다(한 줄 24em × 실제 글자폭 ≈0.88 × 48px ≈ 1010px).
export const PRESENTER_HOST = { reservePct: 40, captionLeftPct: 5, captionWidthRatio: 0.6, captionMaxRows: 2 } as const;

/** 🧍 작업물 아바타의 구도 — 프리셋이면 그 칸, 립싱크 클립(custom)이면 원본 프리셋(custom.presetId · 없으면 imageUrl 대조)의 칸.
 *  HeyGen 사진 타일·모르는 원본 = null(종전 배치). */
export function presenterFramingOf(presenter: unknown): "bust" | null {
  return presenterFramingPresetOf(presenter)?.framing ?? null;
}

/** 🧍 구도를 정하는 프리셋(프리셋 · 립싱크 원본) — 칸 폭 등 구도 값을 함께 읽는다. 없으면 undefined. */
export function presenterFramingPresetOf(presenter: unknown): MsbAvatarPreset | undefined {
  const p = presenter as { presetId?: unknown; custom?: { imageUrl?: unknown; kind?: unknown; presetId?: unknown } } | null | undefined;
  const custom = p?.custom;
  if (custom && typeof custom === "object") {
    if (custom.kind !== "chroma") return undefined;
    if (typeof custom.presetId === "string") return getMsbAvatarPreset(custom.presetId);
    const url = custom.imageUrl;
    return typeof url === "string" ? MSB_AVATAR_PRESETS.find((x) => x.urls.includes(url)) : undefined;
  }
  return typeof p?.presetId === "string" ? getMsbAvatarPreset(p.presetId) : undefined;
}

/**
 * 🏢 이 회사 픽커에 세울 프리셋(#6344) — 범용 + 회사 업종과 겹치는 회사 전용 인물.
 * ★지금 고른 프리셋(`currentId`)은 업종 밖이어도 남긴다★ — 이미 그 인물을 쓴 작업물을 열었을 때 픽커에서
 *   선택 표시가 사라지면 «무엇이 적용돼 있나»를 알 수 없다(막다른 길). 새로 고를 수 없게만 한다.
 * 업종 로딩 중·미지정 = 빈 배열 → 회사 전용 인물 숨김(fail-closed · 스타일 업종 게이트와 같은 극성).
 */
export function pickableAvatarPresets(
  industries: readonly string[],
  currentId: string | null | undefined,
  presets: readonly MsbAvatarPreset[] = MSB_AVATAR_PRESETS,
): MsbAvatarPreset[] {
  return presets.filter((p) => !p.industries || p.id === currentId || p.industries.some((v) => industries.includes(v)));
}

/**
 * 💳 작업실 진행자 카드의 비용 꼬리표 — 장면별 굽기 프리셋은 «무료»가 아니다(본부 10-12: 카드 «· 무료» 바로 아래 ≈5만 크레딧 경고).
 * 숫자는 싣지 않는다 — 금액의 정본은 두 번째 클릭 전 비용 한 줄(단가표 기반)이다.
 */
export function presetCostTag(p: Pick<MsbAvatarPreset, "sceneBake">): string {
  return p.sceneBake ? "장면별 굽기 · 크레딧 사용" : "무료";
}
